<template>
  <AccordionRoot
    v-model="modelValue"
    :type="type"
    :collapsible="collapsible"
    :defaultValue="defaultValue"
    class="Accordion"
  >
    <AccordionItem
      v-for="item of items"
      :key="item.value"
      :value="item.value"
      class="Accordion-item"
    >
      <template #default="{ open }">
        <AccordionHeader>
          <AccordionTrigger asChild>
            <div class="Accordion-header">
              <slot name="header" :item="item">
                <div>
                  <h3 class="Accordion-title">
                    {{ item.title }}
                  </h3>
                  <h4 v-if="item.subtitle" class="Accordion-subtitle">
                    {{ item.subtitle }}
                  </h4>
                  <p
                    v-if="item.description"
                    class="Accordion-description"
                    v-html="item.description"
                  />
                </div>
              </slot>
              <IconButton
                :icon="open ? 'chevron-up' : 'chevron-down'"
                size="lg"
                class="Accordion-toggleButton"
                :ariaLabel="t('ui.components.accordion.toggleAriaLabel')"
              />
            </div>
          </AccordionTrigger>
        </AccordionHeader>

        <AccordionContent class="Accordion-contentWrapper">
          <hr class="Accordion-divider">
          <slot name="content" :item="item">
            <p class="Accordion-content" v-html="item.content" />
          </slot>
        </AccordionContent>
      </template>
    </AccordionItem>
  </AccordionRoot>
</template>

<script setup generic="I extends AccordionItemProp = AccordionItemProp" lang="ts">
import { useI18n } from '@cvo/i18n/composables/useI18n';
import IconButton from '@cvo/ui/components/atoms/IconButton/IconButton.vue';
import { AccordionRoot, AccordionItem, AccordionHeader, AccordionTrigger, AccordionContent } from 'reka-ui';
import type { AccordionProps, AccordionItem as AccordionItemProp } from './Accordion.model';

const props = defineProps<AccordionProps<I>>();

const modelValue = defineModel<string | string[]>();

const { t } = useI18n();
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';


@layer cvo-ui {
  .Accordion {
    &-item {
      padding: vars.$dimensions-d-4;
      border-radius: vars.$molecules-accordion-lg-md-border-radius;
      border: vars.$molecules-accordion-border-stroke solid vars.$molecules-accordion-border-color;
      background-color: vars.$molecules-accordion-bg-color;
      margin-bottom: vars.$dimensions-d-2;

      @include responsive.mobile {
        border-radius: vars.$molecules-accordion-sm-border-radius;
      }
    }

    &-contentWrapper {
      overflow: hidden;

      &[data-state='open'] {
        animation: accordionSlideDown vars.$transition-base ease-out;
      }

      &[data-state='closed'] {
        animation: accordionSlideUp vars.$transition-base ease-in;
      }
    }

    &-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      width: 100%;
    }

    &-title {
      color: vars.$molecules-accordion-title-color;
      // Paragraph M Bold at every size: the design has one combined lg / md / sm variant.
      @include text.text('md', $font-weight: vars.$font-weight-text-500);
    }

    &-subtitle {
      color: vars.$molecules-accordion-subtitle-color;
      @include text.text('sm');
      font-weight: vars.$font-weight-text-500;
    }

    &-description {
      color: vars.$molecules-accordion-description-color;
      @include text.text('xs');
    }

    &-toggleButton {
      color: vars.$molecules-accordion-icon-color;
    }

    &-content {
      color: vars.$molecules-accordion-contentaccordion-text-color;
      @include text.text('sm');
    }

    &-divider {
      margin: vars.$dimensions-d-3 0;
      border: none;
      border-top: vars.$molecules-accordion-border-stroke solid vars.$molecules-accordion-border-color;
    }
  }
}
</style>